import React, { useState, useEffect } from 'react';
import { Calendar, User, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import { getMe, getBookings } from '../services/api';
import { toast } from 'sonner';

export default function PatientDashboard() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, bookingsRes] = await Promise.all([
        getMe(),
        getBookings()
      ]);
      setUser(userRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="font-public text-[#4B5563]">Manage your appointments and healthcare journey</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-[#4A90E2]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">{bookings.length}</p>
                <p className="text-sm text-[#4B5563] font-public">Total Bookings</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-lg flex items-center justify-center">
                <User className="h-6 w-6 text-[#2DD4BF]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">Patient</p>
                <p className="text-sm text-[#4B5563] font-public">Account Type</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0F2A44]/10 rounded-lg flex items-center justify-center">
                <Heart className="h-6 w-6 text-[#0F2A44]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">Active</p>
                <p className="text-sm text-[#4B5563] font-public">Status</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h2 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-6">Your Appointments</h2>
          
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="font-public text-[#4B5563]">No appointments yet</p>
              <p className="font-public text-sm text-[#4B5563] mt-2">Book your first appointment to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.booking_id} className="border border-slate-100 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-manrope font-semibold text-[#0F2A44] mb-1">
                        Booking #{booking.booking_id}
                      </p>
                      <p className="font-public text-sm text-[#4B5563] mb-2">
                        {booking.preferred_date} at {booking.preferred_time}
                      </p>
                      <p className="font-public text-sm text-[#4B5563]">
                        Payment: {booking.payment_method}
                      </p>
                      {booking.reason && (
                        <p className="font-public text-sm text-[#4B5563] mt-2">
                          Reason: {booking.reason}
                        </p>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}